import styles from "./ReferAndEarnPage.module.css";
import { useMemo, useState } from "react";
import { useReferralSettings } from "@/features/referrals/viewmodel";
import QRCode from "react-qr-code";
import { ChevronDownIcon } from "@/assets/svg";
import { Modal } from "@/shared/modal";
import referralHeroHouse from "@/assets/images/referal-image.png";
import howItWorksBg from "@/assets/images/how-it-works-bg.jpg";
import faqHouseBg from "@/assets/images/faq-house-bg.jpg";

const referralFaqs = [
  {
    id: "annual-fee",
    iconType: "dollar",
    question: "Why is there an annual fee for protesting?",
    answer:
      "Property taxes are reassessed annually, and each year presents new opportunities to challenge your assessment. The annual fee covers ongoing monitoring, analysis, and filing support each tax year.",
  },
  {
    id: "every-year",
    iconType: "calendar",
    question: "Do I have to protest every year?",
    answer:
      "You do not have to protest every year, but assessments can change annually. Reviewing your value each year helps ensure you are not overpaying.",
  },
  {
    id: "no-change",
    iconType: "chart",
    question: "What if there is no value change?",
    answer:
      "If there is no meaningful value change, a protest may not be necessary that year. We still monitor future assessments for new savings opportunities.",
  },
  {
    id: "tax-increase",
    iconType: "document",
    question: "Can my property tax go up every year?",
    answer:
      "Tax rates can change year to year, but protesting helps keep your assessed value fair and can reduce over-assessment risk.",
  },
];

// ============================================================================
// Custom Icons & Illustrations matching exact UI
// ============================================================================

const LinkChainIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const CopyIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const ShareIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const EmailIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="5" width="18" height="14" rx="2" ry="2" />
    <polyline points="3 7 12 13 21 7" />
  </svg>
);

const SmsIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    <circle cx="8" cy="12" r="1" fill="currentColor" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
    <circle cx="16" cy="12" r="1" fill="currentColor" />
  </svg>
);

const QrCodeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M9 9h0" />
    <path d="M20 9h0" />
    <path d="M20 20h0" />
    <path d="M9 20h0" />
  </svg>
);

const CoinsStackIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <ellipse cx="8" cy="17" rx="6" ry="2.5" />
    <path d="M2 17v-4c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5v4" />
    <path d="M2 13V9c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5v4" />
    <ellipse cx="16" cy="8" rx="6" ry="2.5" />
    <path d="M10 8v4c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5V8" />
  </svg>
);

const UsersGroupIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CheckCircleSolidIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="#0b7a4b"
    stroke="#ffffff"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="10" stroke="none" />
    <polyline points="8 12 11 15 16 9" stroke="#ffffff" strokeWidth="2.5" />
  </svg>
);

const ChevronCircleRightIcon = () => (
  <div className={styles.chevronCircle}>
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#16a34a"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </div>
);

const InfoTooltipIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#94a3b8"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const DocumentActivityIcon = () => (
  <div className={styles.activityBadgeIcon}>
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#0b7a4b"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  </div>
);

const CalendarIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#64748b"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const EmptyStateIllustration = () => (
  <div className={styles.emptyIllustrationWrap}>
    <div className={styles.emptyCircleBadge}>
      <svg
        width="38"
        height="38"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#475569"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    </div>
    {/* Floating decorative leaves */}
    <span className={`${styles.leafDeco} ${styles.leafLeft}`} />
    <span className={`${styles.leafDeco} ${styles.leafRight}`} />
  </div>
);

const StepPhoneIllustration = () => (
  <div className={styles.stepIllustration}>
    <svg width="68" height="68" viewBox="0 0 72 72" fill="none">
      {/* Hand & Smartphone graphic */}
      <rect x="22" y="10" width="28" height="50" rx="6" fill="#1e293b" />
      <rect x="24" y="14" width="24" height="42" rx="3" fill="#ffffff" />
      {/* Share icon on screen */}
      <circle cx="36" cy="30" r="3" fill="#0b7a4b" />
      <circle cx="31" cy="38" r="2.5" fill="#0b7a4b" />
      <circle cx="41" cy="38" r="2.5" fill="#0b7a4b" />
      <line x1="35" y1="31" x2="32" y2="37" stroke="#0b7a4b" strokeWidth="1.5" />
      <line x1="37" y1="31" x2="40" y2="37" stroke="#0b7a4b" strokeWidth="1.5" />
      {/* Hand thumb holding phone */}
      <path d="M14 42 C14 36, 22 36, 25 40 L25 50 C22 52, 14 50, 14 42 Z" fill="#fed7aa" />
    </svg>
  </div>
);

const StepDocumentIllustration = () => (
  <div className={styles.stepIllustration}>
    <svg width="68" height="68" viewBox="0 0 72 72" fill="none">
      {/* Sheet of paper */}
      <rect
        x="18"
        y="12"
        width="34"
        height="46"
        rx="4"
        fill="#ffffff"
        stroke="#e2e8f0"
        strokeWidth="2"
      />
      <line
        x1="24"
        y1="22"
        x2="38"
        y2="22"
        stroke="#0b7a4b"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <line
        x1="24"
        y1="30"
        x2="44"
        y2="30"
        stroke="#cbd5e1"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="24"
        y1="38"
        x2="44"
        y2="38"
        stroke="#cbd5e1"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="24"
        y1="46"
        x2="36"
        y2="46"
        stroke="#cbd5e1"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Green pen */}
      <g transform="translate(42, 34) rotate(45)">
        <rect x="0" y="0" width="6" height="24" rx="2" fill="#0b7a4b" />
        <polygon points="0,24 6,24 3,30" fill="#f59e0b" />
      </g>
    </svg>
  </div>
);

const StepGiftIllustration = () => (
  <div className={styles.stepIllustration}>
    <svg width="68" height="68" viewBox="0 0 72 72" fill="none">
      {/* Soft circular background glow */}
      <circle cx="36" cy="38" r="24" fill="#f0fdf4" />
      {/* Gift Box Base */}
      <rect x="20" y="32" width="32" height="24" rx="3" fill="#10b981" />
      {/* Gift Box Lid */}
      <rect x="17" y="26" width="38" height="8" rx="2" fill="#059669" />
      {/* Golden Vertical Ribbon */}
      <rect x="33" y="26" width="6" height="30" fill="#facc15" />
      {/* Golden Bow Ribbon */}
      <path d="M36 26 C30 18, 20 20, 34 26 Z" fill="#facc15" />
      <path d="M36 26 C42 18, 52 20, 38 26 Z" fill="#facc15" />
      <circle cx="36" cy="26" r="3" fill="#eab308" />
    </svg>
  </div>
);

const ArrowConnectorIcon = () => (
  <div className={styles.stepArrowWrap}>
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#10b981"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  </div>
);

export const ReferAndEarnPage = () => {
  const {
    referralShareUrl,
    referralCode,
    referralCount,
    referralStats,
    statistics,
    transactions,
    referralsList,
    copied,
    statusFilter,
    setStatusFilter,
    copyReferralCode,
    shareReferralCode,
    openEmail,
    openSMS,
    isLoadingTransactions,
    isLoadingReferrals,
  } = useReferralSettings();

  const [activeTab, setActiveTab] = useState<"activity" | "myRewards">("activity");
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(3);

  const qrValue = referralShareUrl || referralCode || "";

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  const activityRows = useMemo(
    () =>
      (activeTab === "myRewards" ? referralsList : transactions).map((item) => {
        const amount = Number(item.rewards_points ?? item.rewardPointsAwarded ?? 0);
        const source =
          item.title ||
          item.message ||
          item.refered_user?.email ||
          item.referal_user?.email ||
          item.name ||
          "Referral";
        const dateValue = item.created_at || item.completed_at || item.signup_at || "";

        return {
          source,
          date: dateValue ? new Date(dateValue).toLocaleDateString() : "-",
          amount: `${amount >= 0 ? "+" : "-"}$${Math.abs(amount).toFixed(2)}`,
          status: item.status || "pending",
          referal_user: item?.referal_user?.first_name
            ? item?.referal_user?.first_name + " (" + item?.referal_user?.email + ")"
            : "-",
        };
      }),
    [activeTab, referralsList, transactions]
  );

  return (
    <div className={styles.page}>
      {/* ================================================================
          1. HERO BANNER (Full Background Panoramic Villa)
          ================================================================ */}
      <section className={styles.hero} style={{ backgroundImage: `url(${referralHeroHouse})` }}>
        <div className={styles.heroOverlay} />

        <div className={styles.heroLeft}>
          <div className={styles.heroLabel}>REFER A FRIEND</div>
          <h1 className={styles.heroTitle}>
            Earn <span className={styles.heroTitleGreen}>$50 Credits</span>
          </h1>
          <p className={styles.heroSubtitle}>
            For each friend who successfully protests their property tax with us.
          </p>

          <div className={styles.heroActionsRow}>
            {/* Referral Link Box */}
            <div className={styles.linkInputWrapper}>
              <span className={styles.linkChainIcon}>
                <LinkChainIcon />
              </span>
              <input
                value={referralShareUrl || referralCode || "Referral link unavailable"}
                readOnly
                className={styles.linkInput}
                aria-label="Your Referral Link"
              />
              <button
                type="button"
                className={`${styles.copyButton} ${copied ? styles.copyButtonCopied : ""}`}
                onClick={copyReferralCode}
                aria-label="Copy Referral Link"
              >
                <CopyIcon /> {copied ? "Copied" : "Copy"}
              </button>
            </div>

            {/* Social Share Buttons */}
            <div className={styles.iconButtonsGroup}>
              <button
                type="button"
                className={styles.iconAction}
                onClick={shareReferralCode}
                aria-label="Share via social"
              >
                <div className={styles.iconActionCircle}>
                  <ShareIcon />
                </div>
                <span className={styles.iconActionText}>Share</span>
              </button>

              <button
                type="button"
                className={styles.iconAction}
                onClick={openEmail}
                aria-label="Share via Email"
              >
                <div className={styles.iconActionCircle}>
                  <EmailIcon />
                </div>
                <span className={styles.iconActionText}>Email</span>
              </button>

              <button
                type="button"
                className={styles.iconAction}
                onClick={openSMS}
                aria-label="Share via SMS"
              >
                <div className={styles.iconActionCircle}>
                  <SmsIcon />
                </div>
                <span className={styles.iconActionText}>SMS</span>
              </button>

              <button
                type="button"
                className={styles.iconAction}
                onClick={() => setIsQrOpen(true)}
                aria-label="Show QR Code"
              >
                <div className={styles.iconActionCircle}>
                  <QrCodeIcon />
                </div>
                <span className={styles.iconActionText}>QR Code</span>
              </button>
            </div>
          </div>
        </div>

        {/* Hero Right: Floating Motto above the $50 Credits Card */}
        <div className={styles.heroRight}>
          <div className={styles.heroFloatingBadge}>
            <span className={styles.floatingWord}>Share.</span>
            <span className={styles.floatingWord}>Save.</span>
            <span className={styles.floatingWord}>Together.</span>
          </div>
        </div>
      </section>

      {/* ================================================================
          2. STATS ROW (3 Cards with Rounded Icons and Right Chevrons)
          ================================================================ */}
      <section className={styles.statsRow}>
        {/* Total Rewards */}
        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconMint}`}>
            <CoinsStackIcon color="#0b7a4b" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statTitle}>
              Total Rewards <InfoTooltipIcon />
            </div>
            <div className={styles.statValue}>${(statistics?.totalPointsUsed ?? 0).toFixed(2)}</div>
          </div>
          <ChevronCircleRightIcon />
        </div>

        {/* Total Referrals */}
        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconBlue}`}>
            <UsersGroupIcon color="#2563eb" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statTitle}>Total Referrals</div>
            <div className={styles.statValue}>{referralCount}</div>
          </div>
          <ChevronCircleRightIcon />
        </div>

        {/* Successful */}
        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconGreen}`}>
            <CheckCircleSolidIcon />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statTitle}>Successful</div>
            <div className={styles.statValue}>{referralStats.completed}</div>
          </div>
          <ChevronCircleRightIcon />
        </div>
      </section>

      {/* ================================================================
          3. REWARDS ACTIVITY SECTION
          ================================================================ */}
      <section className={styles.activitySection}>
        <div className={styles.activityHeader}>
          <div className={styles.activityTitleWrap}>
            <DocumentActivityIcon />
            <h2 className={styles.activityTitle}>Rewards Activity</h2>

            {/* Pill Tabs */}
            <div className={styles.activityTabs}>
              <button
                type="button"
                className={`${styles.tabButton} ${
                  activeTab === "activity" ? styles.tabButtonActive : ""
                }`}
                onClick={() => setActiveTab("activity")}
              >
                My Rewards Activity
              </button>
              <button
                type="button"
                className={`${styles.tabButton} ${
                  activeTab === "myRewards" ? styles.tabButtonActive : ""
                }`}
                onClick={() => setActiveTab("myRewards")}
              >
                My Referrals
              </button>
            </div>
          </div>

          {/* Date Filter Pill */}
          <div className={styles.dateFilterPill}>
            <CalendarIcon />
            <span>Last 3 Months</span>
            <ChevronDownIcon className={styles.dateChevron} />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          {activeTab === "myRewards" && (
            <div className={styles.statusFilters}>
              <button
                type="button"
                className={`${styles.statusFilterPill} ${
                  statusFilter === "all" ? styles.statusFilterPillActive : ""
                }`}
                onClick={() => setStatusFilter("all")}
              >
                All
              </button>
              <button
                type="button"
                className={`${styles.statusFilterPill} ${
                  statusFilter === "pending" ? styles.statusFilterPillActive : ""
                }`}
                onClick={() => setStatusFilter("pending")}
              >
                Pending
              </button>
              <button
                type="button"
                className={`${styles.statusFilterPill} ${
                  statusFilter === "completed" ? styles.statusFilterPillActive : ""
                }`}
                onClick={() => setStatusFilter("completed")}
              >
                Completed
              </button>
            </div>
          )}

          {/* Table Column Headers */}
          <div className={styles.tableHeader}>
            <span>{activeTab === "myRewards" ? "Reward Source" : "Referral Source"}</span>
            <span>{activeTab === "activity" ? "Referral User" : "-"}</span>
            <span>{activeTab === "myRewards" ? "Date Earned" : "Date Referred"}</span>
            <span>Amount</span>
            <span>Status</span>
          </div>

          {/* Table Content Rows / Empty State */}
          <div className={styles.tableBody}>
            {(activeTab === "myRewards" ? isLoadingReferrals : isLoadingTransactions) && (
              <div className={styles.emptyState}>
                <EmptyStateIllustration />
                <h3 className={styles.emptyStateTitle}>Loading...</h3>
              </div>
            )}

            {!(activeTab === "myRewards" ? isLoadingReferrals : isLoadingTransactions) &&
              activityRows.map((row, idx) => (
                <div key={`${row.source}-${row.date}-${idx}`} className={styles.tableRow}>
                  <span className={styles.colSource}>{row.source}</span>
                  <span className={styles.colUser}>
                    {activeTab === "activity" ? row.referal_user : "-"}
                  </span>
                  <span className={styles.colDate}>{row.date}</span>
                  <span
                    className={
                      row.amount.startsWith("+") ? styles.amountPositive : styles.amountNegative
                    }
                  >
                    {row?.status?.toLowerCase() === "pending" ? "+$0.00" : row.amount}
                  </span>
                  <span className={styles.statusBadge}>{row.status}</span>
                </div>
              ))}

            {!(activeTab === "myRewards" ? isLoadingReferrals : isLoadingTransactions) &&
              activityRows.length === 0 && (
                <div className={styles.emptyState}>
                  <EmptyStateIllustration />
                  <h3 className={styles.emptyStateTitle}>No referral activity found.</h3>
                  <p className={styles.emptyStateDesc}>
                    Share your link and start earning rewards!
                  </p>
                </div>
              )}
          </div>
        </div>
      </section>

      {/* ================================================================
          4. HOW IT WORKS (3 Step Cards with Illustrations & Arrows)
          ================================================================ */}
      <section className={styles.howItWorks} style={{ backgroundImage: `url(${howItWorksBg})` }}>
        <div className={styles.howItWorksOverlay} />

        <div className={styles.howItWorksLeft}>
          <div className={styles.howItWorksLabel}>HOW IT WORKS</div>
          <h2 className={styles.howItWorksTitle}>
            Invite Friends,
            <br />
            Get Rewarded
          </h2>
          <p className={styles.howItWorksSubtitle}>
            It&apos;s simple! Help your friends save on property taxes and earn $50 credits for each
            successful protest.
          </p>

          <button
            type="button"
            className={styles.inviteButton}
            onClick={shareReferralCode}
            aria-label="Invite friends now"
          >
            <span>Invite Now</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>

        <div className={styles.howItWorksSteps}>
          {/* Step 1 */}
          <article className={styles.stepCard}>
            <div className={styles.stepBadge}>1</div>
            <StepPhoneIllustration />
            <h3 className={styles.stepTitle}>Share Your Link</h3>
            <p className={styles.stepDesc}>
              Share your unique referral link with friends via social media or messengers.
            </p>
          </article>

          <ArrowConnectorIcon />

          {/* Step 2 */}
          <article className={styles.stepCard}>
            <div className={styles.stepBadge}>2</div>
            <StepDocumentIllustration />
            <h3 className={styles.stepTitle}>They File a Protest</h3>
            <p className={styles.stepDesc}>
              Your friend uses your link to sign up and file their property tax protest.
            </p>
          </article>

          <ArrowConnectorIcon />

          {/* Step 3 */}
          <article className={styles.stepCard}>
            <div className={styles.stepBadge}>3</div>
            <StepGiftIllustration />
            <h3 className={styles.stepTitle}>You Earn Rewards</h3>
            <p className={styles.stepDesc}>
              Once their protest is filed, you get rewarded with $50 credits.
            </p>
          </article>
        </div>
      </section>

      {/* ================================================================
          5. FREQUENTLY ASKED QUESTIONS SECTION
          ================================================================ */}
      <section className={styles.faqSection}>
        {/* Background House & Landscape in bottom-left */}
        <div className={styles.faqBackground} style={{ backgroundImage: `url(${faqHouseBg})` }} />
        <div className={styles.faqOverlay} />

        <div className={styles.faqLeft}>
          <div className={styles.faqBadgeRow}>
            <div className={styles.faqIconCircle}>?</div>
            <span className={styles.faqPill}>FAQ</span>
          </div>

          <h2 className={styles.faqTitle}>
            Frequently
            <br />
            Asked Questions
          </h2>

          <p className={styles.faqSubtitle}>
            Find answers to common questions about our property tax protest and referral program.
          </p>
        </div>

        <div className={styles.faqList}>
          {referralFaqs.map((item, index) => {
            const isActive = openFaqIndex === index;
            return (
              <div
                key={item.id}
                className={`${styles.faqCard} ${isActive ? styles.faqCardActive : ""}`}
                onClick={() => toggleFaq(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleFaq(index);
                  }
                }}
              >
                <div className={styles.faqCardHeader}>
                  <div
                    className={`${styles.faqCardIcon} ${
                      item.iconType === "dollar"
                        ? styles.iconBgGreen
                        : item.iconType === "calendar"
                          ? styles.iconBgBlue
                          : item.iconType === "chart"
                            ? styles.iconBgPurple
                            : styles.iconBgGreen
                    }`}
                  >
                    {item.iconType === "dollar" && <span>$</span>}
                    {item.iconType === "calendar" && (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="3" ry="3" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    )}
                    {item.iconType === "chart" && (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="20" x2="18" y2="10" />
                        <line x1="12" y1="20" x2="12" y2="4" />
                        <line x1="6" y1="20" x2="6" y2="14" />
                      </svg>
                    )}
                    {item.iconType === "document" && (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                    )}
                  </div>

                  <div className={styles.faqCardContent}>
                    <div className={styles.faqCardTitle}>{item.question}</div>
                    <div className={styles.faqCardAnswer}>{item.answer}</div>
                  </div>

                  <div
                    className={`${styles.faqChevronWrap} ${
                      isActive ? styles.faqChevronActive : ""
                    }`}
                  >
                    <ChevronDownIcon className={styles.faqChevron} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ================================================================
          6. PAGE FOOTER
          ================================================================ */}
      <footer className={styles.footer}>
        <div className={styles.footerLeft}>
          <span>© 2026 ANDY. All rights reserved.</span>
          <span className={styles.footerDivider}>|</span>
          <a href="#" className={styles.footerLink}>
            Privacy Policy
          </a>
        </div>
        <div className={styles.footerRight}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#10b981" stroke="none">
            <path d="M11 20A7 7 0 0 1 4 13C4 8.5 7.5 3 12 3s8 5.5 8 10a7 7 0 0 1-7 7z" />
            <path d="M12 3v17" stroke="#ffffff" strokeWidth="1.5" />
          </svg>
          <span>Helping homeowners save, together.</span>
        </div>
      </footer>

      {/* ================================================================
          7. QR CODE MODAL
          ================================================================ */}
      <Modal
        isOpen={isQrOpen}
        onClose={() => setIsQrOpen(false)}
        showCloseButton={false}
        unconstrained
        className={styles.qrModal}
        bodyClassName={styles.qrModalBody}
        ariaLabel="Referral QR code"
      >
        <button
          type="button"
          className={styles.qrClose}
          onClick={() => setIsQrOpen(false)}
          aria-label="Close QR modal"
        >
          &times;
        </button>
        <div className={styles.qrTitle}>Scan to invite</div>
        <div className={styles.qrImageWrap}>
          {qrValue ? (
            <QRCode
              size={180}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              value={qrValue}
              viewBox="0 0 256 256"
            />
          ) : (
            <div className={styles.qrEmpty}>Referral link unavailable.</div>
          )}
        </div>
        <div className={styles.qrLink} title={qrValue}>
          {qrValue || "-"}
        </div>
      </Modal>
    </div>
  );
};
